import React, {
  createContext, useState, useEffect, useContext, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import moment from 'moment-timezone';
import { ThemeProvider } from 'styled-components';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { TranslateUrlsContext } from 'gatsby-plugin-translate-urls';
import { isMultilingual } from '../../languages';
import theme from './theme';
import getSeoConfig from '../seoConfig';
import routes from '../Routes';
import { mobileThreshold, tabletThreshold } from '../components/home/v4/styledComponents';

import {
  helpScoutContactForm, googleTagManagerScript, googleTagManagerNoScript,
} from '../vendors';
import withCustomerContext from '../withCustomerContext';
import withOrderContext from '../withOrderContext';

import '../polyfill/trimStart';
import '../assets/flaticon/flaticon.css';

const disableScripts = () => typeof window === 'undefined'
  || ['/nos-tarifs-app/', '/tilli-votre-partenaire-retouche-domicile-boutique-ecommerce/'].includes(window.location.pathname);

const disableScriptsExceptOnProd = () => typeof window === 'undefined' || !(process.env.GATSBY_ENV === 'PROD');

const isMobile = () => typeof window !== 'undefined' ? window.outerWidth < mobileThreshold : true;

const isTablet = () => typeof window !== 'undefined' ? window.outerWidth < tabletThreshold : true;

const getRoute = (routeSlug) => routes[routeSlug];

const languageMap = {
  en: 'english',
  de: 'german',
  es: 'spanish',
  it: 'italian',
  pt: 'portuguese',
};

const getTitle = (routeSlug, post, seoConfig, language) => {
  if (!routeSlug) return seoConfig.title;
  if (!getRoute(routeSlug)) return post.seo.title;
  const { seo = {} } = getRoute(routeSlug);
  const languageSeo = seo?.[languageMap?.[language]] || {};
  return languageSeo.title || seo.title || seoConfig.title;
};

const getDescription = (routeSlug, post, seoConfig, language) => {
  if (!routeSlug) return seoConfig.description;
  if (!getRoute(routeSlug)) return post.seo.description;
  const { seo = {} } = getRoute(routeSlug);
  const languageSeo = seo?.[languageMap?.[language]] || {};
  return languageSeo.description || seo.description || seoConfig.description;
};

const getSeo = (routeSlug, post, seoConfig, language) => {
  if (!routeSlug) return seoConfig.description;
  if (!getRoute(routeSlug)) return post.seo;
  const { seo = {} } = getRoute(routeSlug);

  const languageSeo = seo?.[languageMap?.[language]] || {};
  return {
    ...seoConfig,
    ...seo,
    ...languageSeo,
  };
};

const getUrl = (routeSlug, post, seoConfig) => {
  if (!routeSlug) return seoConfig.baseUrl;
  if (!getRoute(routeSlug)) return post.path;
  const { url } = getRoute(routeSlug);
  return `${seoConfig.baseUrl}${url}`;
};

const createPostsScriptMarkup = (postSeo, seoConfig) => {
  const {
    title, description, mainEntityOfPageId, image, datePublished, dateModified,
    authorName, publisherName, publisherLogoUrl,
  } = postSeo;
  const markup = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${seoConfig.baseUrl}${mainEntityOfPageId}`,
    },
    headline: title,
    image: image && image.map((img) => `${seoConfig.baseUrl}${img}`),
    datePublished,
    dateModified,
    author: {
      '@type': 'Person',
      name: authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: `${seoConfig.baseUrl}${publisherLogoUrl}`,
      },
    },
    description,
  };
  return JSON.stringify(markup);
};

const createScriptMarkup = (city = 'Paris') => {
  const markup = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    url: 'https://tilli.fr/',
    logo: 'https://tilli.fr/logo.png',
    areaServed: 'FR',
    location: {
      '@type': 'Place',
      name: 'Tilli',
      address: city,
    },
    sameAs: [
      'https://www.facebook.com/tilli.couture/',
      'https://www.instagram.com/tilli.jointhecycle/',
      'https://twitter.com/tillicouture',
      'https://www.youtube.com/channel/UCdC9FGiK_u8-j_H4UCrdnWA',
    ],
  };
  return JSON.stringify(markup);
};

function getUrls() {
  if (process.env.GATSBY_ENV === 'PROD') {
    return {
      fr: 'https://tilli.fr',
      en: 'https://tilli.com',
    };
  }
  if (process.env.GATSBY_ENV === 'STAGING') {
    return {
      fr: 'https://staging.tilli.fr',
      en: 'https://staging.tilli.com',
    };
  }
  return {
    fr: 'http://localhost:3003',
    en: 'http://localhost:3004',
  };
}

export function getBaseUrl() {
  const urls = getUrls();
  return isMultilingual ? urls.en : urls.fr;
}

export const AppContext = createContext({
  isMobile: true,
  isTablet: true,
});

// eslint-disable-next-line max-lines-per-function
function Layout({
  children,
  routeSlug,
  routeUrl,
  post,
  customerContext,
  orderContext,
  city,
}) {
  const [deviceState, setDeviceState] = useState({
    isMobile: true,
    isTablet: true,
  });

  const { t, i18n: { language } } = useTranslation();

  const handleResize = () => {
    setDeviceState({
      isMobile: isMobile(),
      isTablet: isTablet(),
    });
  };

  useEffect(() => {
    const { fetchCustomer } = customerContext;
    const { fetchCountry } = orderContext;

    const landingPageExpiryDate = moment(
      localStorage.getItem('landingPage_expiryKey'),
    );
    if (moment().add(-2, 'day').isAfter(landingPageExpiryDate)) {
      localStorage.removeItem('landingPage_expiryKey');
      localStorage.removeItem('landingPage');
    }

    fetchCustomer();
    fetchCountry();
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [customerContext.fetchCustomer, orderContext.fetchCountry]);

  const seoConfig = getSeoConfig(language);

  const seo = getSeo(routeSlug, post, seoConfig, language);
  const appContext = useMemo(() => ({
    isMobile: deviceState.isMobile,
    isTablet: deviceState.isTablet,
  }), [deviceState]);
  const lang = isMultilingual ? 'en' : 'fr';

  const title = t(getTitle(routeSlug, post, seoConfig, language));
  const description = t(getDescription(routeSlug, post, seoConfig, language));
  const url = routeUrl || getUrl(routeSlug, post, seoConfig);
  const ogType = seo.ogType || 'website';
  const photo = seo.image
    ? `${seoConfig.baseUrl}${seo.image[0]}`
    : `${seoConfig.baseUrl}/images/default-og-image.jpg`;
  const photoFullUrl = photo;
  const { locales, originalUrl, translateUrl } = useContext(TranslateUrlsContext);
  const { en, fr } = getUrls();

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <html lang={lang} />
        {isMultilingual && locales
          .filter((lng) => lng !== 'fr')
          .map((lng) => (
            <link
              rel="alternate"
              hrefLang={lng}
              href={en + translateUrl(originalUrl, lng)}
              key={lng}
            />
          ))}
        <link rel="alternate" hrefLang="fr" href={fr + originalUrl} />
        <meta content={title} property="title" />
        <meta content={description} name="description" />

        {/* Facebook OpenGraph (OG) / Pinterest / LinkedIn */}
        <meta property="og:type" content={ogType} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content={title} />
        <meta property="og:image" content={photo} />
        <meta property="fb:admins" content="562573283921131" />

        {/* Pinterest tracking */}
        <meta
          name="p:domain_verify"
          content="40c7ae5866b0a286d980f9d07244dacc"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@tillicouture" />
        <meta name="twitter:creator" content="@tillicouture" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={photoFullUrl} />

        <meta name="apple-itunes-app" content="app-id=1280578121" />
      </Helmet>

      {!disableScripts() && (
        <Helmet>
          <script>{helpScoutContactForm}</script>
        </Helmet>
      )}

      {!disableScriptsExceptOnProd() && (
        <>
          <Helmet>
            <script
              src="https://browser.sentry-cdn.com/5.12.1/bundle.min.js"
              integrity="sha384-y+an4eARFKvjzOivf/Z7JtMJhaN6b+lLQ5oFbBbUwZNNVir39cYtkjW1r6Xjbxg3"
              crossOrigin="anonymous"
              onLoad="Sentry.init({ dsn: 'https://04d154c0fb284855a70b80991c14f2b5@sentry.io/158955' });"
            />
            <script>{googleTagManagerScript}</script>
          </Helmet>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={{ __html: googleTagManagerNoScript }} />
        </>
      )}

      {seo.datePublished && (
        <script type="application/ld+json">
          {createPostsScriptMarkup(seo, seoConfig)}
        </script>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: createScriptMarkup(city || 'Paris'),
        }}
      />

      {/* cSpell:disable-next-line */}
      <GoogleOAuthProvider clientId="292364543868-iqrgt529jj9jnif8ifo60qcb8msunpg8.apps.googleusercontent.com">
        <ThemeProvider theme={theme}>
          <AppContext.Provider value={appContext}>
            {children}
          </AppContext.Provider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </div>
  );
}

Layout.propTypes = {
  children: PropTypes.node,
  routeSlug: PropTypes.string,
  routeUrl: PropTypes.string,
  customerContext: PropTypes.shape({
    fetchCustomer: PropTypes.func,
  }).isRequired,
  orderContext: PropTypes.shape({
    fetchCountry: PropTypes.func,
  }).isRequired,
  post: PropTypes.shape({
    seo: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    path: PropTypes.string,
  }),
  city: PropTypes.string,
};

Layout.defaultProps = {
  children: null,
  routeSlug: null,
  routeUrl: null,
  post: null,
  city: null,
};

export default withOrderContext(withCustomerContext(Layout));
